import { cn, Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@1trading/react';
import { closeOnly } from '@/components/dialogs/close-only';
import { CopyButton } from '@/components/ui/copy-button';
import { LabeledValue } from '@/components/ui/labeled-value';
import { StatusChip } from '@/components/ui/status-chip';
import { CHIP_CLASS, chargeStatusVariant, formatEurString, fundsStatusVariant, UNKNOWN_CHARGE_EXPLANATION, userStatusVariant } from '@/features/fee-runs/fee-run-helpers';
import type { FeeRunCharge, FeeRunUser } from '@/services/api/fee-runs';
import { formatDate } from '@/utils/format';
import { formatSnakeCase } from '@/utils/string';

/** A charge that takes the whole balance: the account holder is left with nothing in this coin. */
const isEmptied = (charge: FeeRunCharge) => charge.status === 'CHARGED' && Number(charge.balanceAfter) === 0;

/** One account holder's plan and, once collected, the outcome of every charge. */
export const FeeRunUserDialog = ({ user, onClose }: { user: FeeRunUser | null; onClose: () => void }) => {
  const charges = user ? Object.values(user.charges) : [];
  const hasUnknownCharge = charges.some((charge) => charge.status === 'UNKNOWN');

  return (
    <Dialog onOpenChange={closeOnly(onClose)} open={user !== null}>
      <DialogContent className="flex max-h-[85vh] max-w-4xl flex-col">
        {user ? (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 font-mono text-base">
                {user.pid}
                <CopyButton label={`Copy ${user.pid}`} value={user.pid} />
              </DialogTitle>
              <DialogDescription>
                {user.entity} · {user.feeType} fee · {formatSnakeCase(user.entityReason)}
              </DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
              <LabeledValue label="Status">
                <StatusChip className={CHIP_CLASS} variant={userStatusVariant(user.status)}>
                  {formatSnakeCase(user.status)}
                </StatusChip>
              </LabeledValue>
              <LabeledValue label="Ledger">
                <StatusChip className={CHIP_CLASS} variant={fundsStatusVariant(user.fundsStatus)}>
                  {formatSnakeCase(user.fundsStatus)}
                </StatusChip>
              </LabeledValue>
              <LabeledValue label="Balance in scope">{formatEurString(user.totalEurBalance)}</LabeledValue>
              <LabeledValue label="Final fee">{formatEurString(user.finalFeeEur)}</LabeledValue>
              <LabeledValue label="Account status">{formatSnakeCase(user.userStatus)}</LabeledValue>
              <LabeledValue label="Adjustment">{formatSnakeCase(user.adjustment)}</LabeledValue>
              <LabeledValue label="Main FAST id" mono>
                {user.mainFastId}
              </LabeledValue>
              <LabeledValue label="Attempts">{user.attempts ?? 0}</LabeledValue>
            </div>
            {hasUnknownCharge ? <p className="text-sm text-amber-500">{UNKNOWN_CHARGE_EXPLANATION}</p> : null}
            {user.error ? <p className="text-sm text-red-500">{user.error}</p> : null}
            <div className="min-h-0 flex-1 overflow-auto rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Source</TableHead>
                    <TableHead>Coin</TableHead>
                    <TableHead>FAST id</TableHead>
                    <TableHead className="text-right">Balance</TableHead>
                    <TableHead className="text-right">Fee</TableHead>
                    <TableHead className="text-right">Fee (EUR)</TableHead>
                    <TableHead className="text-right">Remaining</TableHead>
                    <TableHead>Outcome</TableHead>
                    <TableHead>Attempted</TableHead>
                    <TableHead>Charged at</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {charges.map((charge) => (
                    <TableRow key={charge.key}>
                      <TableCell className="whitespace-nowrap">{charge.source === 'instant-trade' ? 'Instant trade' : 'Exchange'}</TableCell>
                      <TableCell className="font-medium">
                        {charge.coin}
                        {charge.capped ? <span className="ml-1 text-xs text-muted-foreground">capped</span> : null}
                        {charge.dust ? <span className="ml-1 text-xs text-muted-foreground">dust</span> : null}
                      </TableCell>
                      <TableCell className="font-mono text-xs">{charge.uid}</TableCell>
                      <TableCell className="text-right tabular-nums">{charge.balance}</TableCell>
                      <TableCell className="text-right tabular-nums">{charge.feeAmount}</TableCell>
                      <TableCell className="text-right tabular-nums">{formatEurString(charge.feeEur)}</TableCell>
                      <TableCell className={cn('text-right tabular-nums', isEmptied(charge) && 'text-amber-500')}>
                        {charge.status === 'CHARGED' ? charge.balanceAfter : charge.balance}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-1">
                          <StatusChip className={CHIP_CLASS} variant={chargeStatusVariant(charge.status)}>
                            {formatSnakeCase(charge.status)}
                          </StatusChip>
                          {charge.skipReason ? <span className="text-xs text-muted-foreground">{formatSnakeCase(charge.skipReason)}</span> : null}
                          {charge.error ? <span className="text-xs text-red-500">{charge.error}</span> : null}
                        </div>
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-xs tabular-nums">{charge.attemptedAt ? formatDate(charge.attemptedAt) : '—'}</TableCell>
                      <TableCell className="whitespace-nowrap text-xs tabular-nums">{charge.chargedAt ? formatDate(charge.chargedAt) : '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};
FeeRunUserDialog.displayName = 'FeeRunUserDialog';
